import React, { useState, useEffect } from "react";
import Link from "next/link";
import { toast } from "@jammable/ui-core";
import { useModal } from "@/context/ModalContext";
import { useAuth } from "../../context/AuthProvider";
import { getSubscriptionStatus } from "../../utils/tools";
import { useUpgradeOptions } from "../../../../packages/ui/features/payments/src/hooks/useUpgradeOptions";
import { mutationSubscriptionUpgrade } from "../../../../packages/ui/api/src/payments/mutation";
import {
  getPrice,
  getCurrencySymbol,
  formatPriceWithLocale,
  chargeCurrency,
  getAnnualPrice,
  isAnnualAvailable,
} from "../../utils/pricing";
import VoiceImageJs from "../VoiceImage";
import { useQuery } from "react-query";
import { useWelcomeBack } from "../upsell/useWelcomeBack";
import { songTitle, voiceName } from "@/utils/songTitle";
import { formatHoldLeft } from "@/utils/lockedCover";
import { PlanCTA, checkoutError, startCheckout } from "../pricing/PricingCard";
import { CreditCheckout } from "./CreditCheckoutModal";
import { Spinner } from "@jammable/ui-components/Loader";
import {
  ArrowLeft,
  Check,
  Gift,
  Clapperboard,
  Lock,
  Mic,
  Music,
  Sparkles,
  Type,
} from "lucide-react";

const VoiceImage = VoiceImageJs;

const PLANS = {
  basic: {
    plan: "basic",
    label: "Basic",
    priceTier: "basic",
    discountTier: "basic-discount",
    perks: [
      "Unlimited covers",
      "Every voice",
      "Downloads",
      "Duets and text to speech",
    ],
  },
  creator: {
    plan: "creator",
    label: "Creator",
    priceTier: "creator",
    discountTier: "creator-discount",
    perks: [
      "Train unlimited voices",
      "Marble Run and lyric videos",
      "50 video coins a month",
      "Everything in Basic",
    ],
  },
  power: {
    plan: "power",
    label: "Power",
    priceTier: "power",
    discountTier: null,
    perks: [
      "Priority processing",
      "Unlimited video coins",
      "$25 API credits a month",
      "Everything in Creator",
    ],
  },
};
const PLAN_ORDER = ["basic", "creator", "power"];

// Phones: the action block (plan cards and button, or the pay button) pins
// to the bottom of the screen over a fade; desktop leaves it in the card.
const PINNED =
  "tw-sticky tw-bottom-0 tw-z-10 -tw-mx-[18px] tw-mt-auto tw-flex tw-flex-col tw-gap-2.5 tw-bg-gradient-to-t tw-from-[#150828] tw-from-80% tw-to-transparent tw-px-[18px] tw-pb-[max(16px,env(safe-area-inset-bottom))] tw-pt-8 md:tw-static md:tw-mx-0 md:tw-mt-0 md:tw-gap-3.5 md:tw-bg-none md:tw-p-0";

// What the full-screen paywall lists under "What you get with <plan>".
const HIGHLIGHTS = {
  basic: [
    "Unlimited covers",
    "93,000+ voices",
    "Downloads",
    "Duets and text to speech",
  ],
  creator: [
    "Unlimited covers",
    "93,000+ voices",
    "Train your voices",
    "1080p videos",
    "50 video coins a month",
    "Marble Run and lyric videos",
  ],
  power: [
    "Priority processing",
    "Unlimited video coins",
    "$25 API credits a month",
    "Everything in Creator",
  ],
};

const REASONS = {
  // The studio's locked cover: nothing has been made yet, it starts the
  // moment they subscribe (thankyou.tsx sends the saved request).
  unlock: {
    title: "Unlock your cover",
    body: "Your voice and audio are set. Subscribe and your cover starts straight away, ready in about a minute. Then make as many as you like.",
    plan: "creator",
    cta: "Unlock my cover",
    Icon: Lock,
  },
  create: {
    title: "Your cover is ready to make",
    body: "Your audio and voice are set up. Pick a plan to make it, then make as many covers as you like.",
    plan: "creator",
    Icon: Music,
  },
  browse: {
    title: "Make covers with any voice",
    body: "Unlimited covers, downloads and all 93,000+ voices. Cancel any time.",
    plan: "creator",
    Icon: Music,
  },
  credits: {
    title: "You've used your credits",
    body: "Your next cover is waiting. Basic has no limit, so you never count credits again.",
    plan: "creator",
    cta: "Keep going",
    Icon: Sparkles,
  },
  words: {
    title: "Make the voice say your words",
    body: "Type words or describe a song and the voice sings or says it. Included from Basic.",
    plan: "creator",
    Icon: Type,
  },
  video: {
    title: "Turn it into a video",
    body: "Marble Run and lyric videos, ready for TikTok and Reels. Creator includes 50 video coins every month.",
    plan: "creator",
    Icon: Clapperboard,
  },
  // From the bar shown after someone left the payment form
  // (components/upsell/ReturnBar), which also picks their plan.
  resume: {
    title: "Pick up where you left off",
    body: "Your plan is saved. Finish checking out, then make as many covers as you like.",
    plan: "creator",
    Icon: Sparkles,
  },
  train: {
    title: "Make a voice nobody else has",
    body: "Train your own voices from a few samples and use them in any cover. Unlimited on Creator.",
    plan: "creator",
    Icon: Mic,
  },
};

// Surfaces that open the paywall when the visitor is not mid-way through
// making a cover (the header button, the conversion page). "Your cover is
// ready to make" would be untrue there.
const BROWSE_SOURCES = /^(header-upgrade|conversion-page|welcome-back)/;

/**
 * Pick headline, body and recommended plan from what the caller passed. The
 * callers only send `from` and a free-text `message`, so this matches on
 * the wording they use today (see the UpgradeModal call sites). There are no
 * free covers any more (removed 2026-06-02): `dailyLimit` and any "free
 * daily" message are treated as out of credits and never shown verbatim.
 */
export const resolveUpgradeReason = ({
  from = "",
  message = "",
  dailyLimit,
}) => {
  const msg = message || "";
  if (from === "locked-cover") return REASONS.unlock;
  if (from === "checkout-return") return REASONS.resume;
  if (
    /video|coin|marble/i.test(msg) ||
    msg.includes("Creator") ||
    /video|credit-pack/i.test(from)
  ) {
    return REASONS.video;
  }
  if (/train/i.test(msg) || /train/i.test(from)) return REASONS.train;
  if (dailyLimit || /credits|free daily|continue/i.test(msg)) {
    return REASONS.credits;
  }
  if (/text to speech|tts|generate|song/i.test(msg)) return REASONS.words;
  if (!msg || /unlimited covers/i.test(msg)) {
    return BROWSE_SOURCES.test(from) ? REASONS.browse : REASONS.create;
  }
  return { ...REASONS.browse, title: msg };
};

// Optional, from the locked cover's page:
// - `voices`: the cover being unlocked, shown in the hero.
// - `coverName`: its name, the headline (cleaned with utils/songTitle).
// - `heldUntil`: when its 48h hold ends (utils/lockedCover, enforced by the
//   unlock route), shown as a live countdown.
// - `plan`: the plan to start on, instead of the recommended one.
// - `via`: what opened it, sent as the checkout's `from` (Stripe metadata)
//   in place of "upgrade-modal".
// - `autoCheckout`: go straight to the card form on the starting plan (the
//   bar that brings people back to a checkout they left: one tap, not two).
// - `exitGuard`: an object the opener's ModalContext onBeforeClose asks
//   (`exitGuard.attempt()`); the first close shows "leave without your
//   cover?" instead of closing.
/**
 * @param {{ message?: string, from?: string, dailyLimit?: boolean, voices?: any[] | null, coverName?: string | null, heldUntil?: string | Date | null, plan?: string | null, via?: string | null, autoCheckout?: boolean, exitGuard?: { attempt?: () => boolean } | null }} props
 */
const UpgradeModal = ({
  message = "",
  from,
  dailyLimit = false,
  voices = null,
  coverName = null,
  heldUntil = null,
  plan = null,
  via = null,
  autoCheckout = false,
  exitGuard = null,
}) => {
  const { closeModal } = useModal();
  const { user } = useAuth();
  const status = user?.subscriptions
    ? getSubscriptionStatus(user.subscriptions)
    : null;

  // Someone whose paid plan ended: Basic and Creator at the welcome-back
  // prices (lib/welcomeBack) instead of the full prices.
  // Checkout asks the server for it again, so this only sets what's shown.
  const uid = user?.firebaseUid;
  const { data: welcome, isLoading: welcomeLoading } = useWelcomeBack(
    uid,
    !status || status === "free",
  );

  const {
    currency: detectedCurrency,
    isLoadingPrices,
    promoApplies,
  } = useUpgradeOptions({ from });
  // The first-month promo is withheld at charge time for anyone who has paid
  // before (createSubscriptionIntent -> resolveIsFirstTime). Existing
  // subscribers always get it on upgrade, so the server reports the truth for
  // both cases; default to showing it only while that answer is still loading.
  const showPromo = promoApplies !== false;
  const currency = detectedCurrency?.toUpperCase() || "USD";

  const [clientSecret, setClientSecret] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const [upgrading, setUpgrading] = useState(false);

  const reason = resolveUpgradeReason({ from, message, dailyLimit });

  // Never recommend the plan the visitor already has (or a lower one): step
  // up to the next plan. Power users have nothing to upgrade to, so they get
  // the full list straight away.
  const statusRank = PLAN_ORDER.indexOf(status) + 1;
  const recommendedKey =
    PLAN_ORDER.indexOf(reason.plan) + 1 > statusRank
      ? reason.plan
      : PLAN_ORDER[statusRank] || null;

  // Live countdown of the cover's hold.
  const holdEnds = heldUntil ? new Date(heldUntil) : null;
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!holdEnds) return;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heldUntil]);

  // The first attempt to close shows the leaving step; the second closes.
  const [leaving, setLeaving] = useState(false);
  const [leaveSeen, setLeaveSeen] = useState(false);
  // The yearly gift on the leave step: "arriving" (a short wrapping
  // animation), "closed" (the box), "opening" (shake and burst), "open".
  const [giftPhase, setGiftPhase] = useState("arriving");
  // Claiming the gift: the button keeps spinning while the checkout mounts
  // behind the gift, which steps away once Stripe's form is ready (or after
  // 8s if it never says so), so the card fields are there when it does.
  const [giftClaiming, setGiftClaiming] = useState(false);
  const [paymentReady, setPaymentReady] = useState(false);
  useEffect(() => {
    if (!leaving) return;
    if (giftPhase !== "arriving" && giftPhase !== "opening") return;
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const t = setTimeout(
      () => setGiftPhase(giftPhase === "arriving" ? "closed" : "open"),
      reduce ? 0 : giftPhase === "arriving" ? 1100 : 1150,
    );
    return () => clearTimeout(t);
  }, [leaving, giftPhase]);
  useEffect(() => {
    setPaymentReady(false);
    if (clientSecret && leaving) setLeaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSecret]);
  useEffect(() => {
    if (!giftClaiming || !clientSecret || paymentReady) return;
    const t = setTimeout(() => setPaymentReady(true), 8000);
    return () => clearTimeout(t);
  }, [giftClaiming, clientSecret, paymentReady]);
  useEffect(() => {
    if (paymentReady && giftClaiming) setGiftClaiming(false);
  }, [paymentReady, giftClaiming]);
  useEffect(() => {
    if (!exitGuard) return;
    exitGuard.attempt = () => {
      // Shown once: after the leave step (or its yearly offer) has been
      // seen, closing closes.
      if (leaving || leaveSeen) return true;
      setLeaving(true);
      setLeaveSeen(true);
      return false;
    };
  }, [exitGuard, leaving, leaveSeen]);

  // Real social proof: covers finished today (cached server-side).
  const { data: coversToday } = useQuery(
    ["covers-today"],
    async () => {
      const r = await fetch("/api/stats/coversToday");
      return (await r.json())?.count ?? null;
    },
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false },
  );
  // The main card switches between Basic and Creator (the step up is one
  // tap away at the moment of decision); Power lives under "See all plans".
  // The welcome-back price for a plan card, if it has one.
  const welcomeFor = (p) =>
    !welcome || p?.annual
      ? null
      : p?.plan === "basic"
        ? welcome
        : p?.plan === "creator"
          ? welcome.creator
          : null;
  const welcomeOn = (p) => !!welcomeFor(p);
  // Back to the plan they had: Creator for people whose last plan was.
  const defaultKey = PLANS[plan]
    ? plan
    : welcome
      ? welcome.wasCreator && welcome.creator
        ? "creator"
        : "basic"
      : recommendedKey;
  const [chosenKey, setPlanKey] = useState(null);
  const planKey = chosenKey ?? defaultKey;

  // One tap back to the form: start the checkout as soon as prices are in.
  // If it fails they get the plan step, as if they'd opened it themselves.
  const [autoFailed, setAutoFailed] = useState(false);
  const autoStarted = React.useRef(false);
  const autoPending =
    autoCheckout &&
    !clientSecret &&
    !autoFailed &&
    (!status || status === "free") &&
    !!PLANS[planKey];
  useEffect(() => {
    if (!autoPending || isLoadingPrices || welcomeLoading) return;
    if (autoStarted.current) return;
    autoStarted.current = true;
    const p = PLANS[planKey];
    startCheckout({
      plan: p.plan,
      currency,
      source: via || "upgrade-modal",
      offer: welcomeOn(p) ? "welcome-back" : null,
    })
      .then((secret) => {
        setSelectedPlan(p);
        setClientSecret(secret);
      })
      .catch((e) => {
        toast.error(checkoutError(e));
        setAutoFailed(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPending, isLoadingPrices, welcomeLoading]);

  const handleUpgradeClick = async (upgradeTarget) => {
    try {
      setUpgrading(true);
      await mutationSubscriptionUpgrade(
        upgradeTarget,
        undefined,
        undefined,
        `upgrade-modal${from ? `:${from}` : ""}`,
      );
      toast.success("Your subscription has been updated!");
      closeModal("upgrade-modal");
    } catch (e) {
      toast.error("Something went wrong, please try again.");
      setUpgrading(false);
    }
  };

  // Checkout happens inside the paywall (the offer panel becomes the Stripe
  // form) rather than in a second modal on top of it.
  const checkoutPrice = !selectedPlan
    ? 0
    : welcomeOn(selectedPlan)
      ? welcomeFor(selectedPlan).major
      : selectedPlan.annual
        ? getAnnualPrice(selectedPlan.plan, currency)
        : getPrice(
            chargeCurrency(selectedPlan.plan, currency),
            showPromo || !selectedPlan.discountTier
              ? selectedPlan.priceTier
              : selectedPlan.discountTier,
          );

  if (isLoadingPrices || welcomeLoading || autoPending) {
    // The same dark full screen as the loaded paywall (ModalContext makes
    // the dialog box itself transparent and full screen).
    return (
      <div className="tw-flex tw-min-h-[100dvh] tw-w-full tw-items-center tw-justify-center tw-bg-[#150828]">
        <Spinner size={6} className="tw-border-jam-lime" />
      </div>
    );
  }

  const fmt = (n) => formatPriceWithLocale(n, currency);
  // A plan's prices are shown in the currency it's charged in (USD where
  // Stripe has no local price for it; see chargeCurrency).
  const curOf = (p) =>
    welcomeOn(p)
      ? welcome.currency
      : chargeCurrency(p.plan, currency, p.annual ? "year" : "month");
  const fmtFor = (p, n) => formatPriceWithLocale(n, curOf(p));
  // Same price rules the three-card view always used. A welcome-back price
  // is struck against the plan's standing price, when we sell the plan in
  // that currency.
  const priceOf = (p) =>
    welcomeOn(p)
      ? welcomeFor(p).major
      : showPromo || !p.discountTier
        ? getPrice(curOf(p), p.priceTier)
        : getPrice(curOf(p), p.discountTier);
  const originalOf = (p) =>
    welcomeOn(p)
      ? chargeCurrency(p.plan, welcome.currency) === welcome.currency
        ? getPrice(welcome.currency, p.discountTier)
        : null
      : showPromo && p.discountTier
        ? getPrice(curOf(p), p.discountTier)
        : null;
  // The renewal terms next to the pay button.
  const renewalOf = (p) =>
    welcomeOn(p)
      ? `${fmtFor(p, priceOf(p))} a month for as long as you stay. Renews monthly until you cancel.`
      : `Then ${fmtFor(p, originalOf(p) ?? priceOf(p))} a month. Renews monthly until you cancel.`;

  const ctaProps = (p) => ({
    plan: p.plan,
    status,
    currency,
    setClientSecret,
    onSelect: () => setSelectedPlan(p),
    source: via || "upgrade-modal",
    onUpgradeClick: handleUpgradeClick,
    upgradeLoading: upgrading,
    offer: welcomeOn(p) ? "welcome-back" : null,
  });

  // Free and logged-out visitors are starting a subscription: show what
  // they pay today and when it renews. (status is "free" for free users.)
  const startingNew = !status || status === "free";
  const rec = planKey ? PLANS[planKey] : null;
  // The plan cards: the two plans above what they have now (Basic and
  // Creator for anyone starting fresh), Power only once it's the next step.
  // The recommended one is pre-selected.
  const switchable = PLAN_ORDER.filter(
    (k) => PLAN_ORDER.indexOf(k) + 1 > statusRank,
  ).slice(0, 2);
  const recPrice = rec ? priceOf(rec) : null;
  const recLabel = rec
    ? reason.cta
      ? `${reason.cta} for ${fmtFor(rec, recPrice)}`
      : welcomeOn(rec)
        ? rec.plan === "creator"
          ? `Come back to Creator for ${fmtFor(rec, recPrice)}`
          : `Come back for ${fmtFor(rec, recPrice)}`
        : rec.plan === "basic"
          ? `Start for ${fmtFor(rec, recPrice)}`
          : `Start ${rec.label} for ${fmtFor(rec, recPrice)}`
    : null;
  const ReasonIcon = reason.Icon;
  const shownVoices = (voices || []).slice(0, 3);

  const isCover = reason === REASONS.unlock || reason === REASONS.create;
  // The welcome-back line, by why their plan ended (lib/welcomeBack).
  const welcomeLine = () => {
    const toCreator = welcome.wasCreator && welcome.creator;
    const k = toCreator ? PLANS.creator : PLANS.basic;
    const offer = toCreator
      ? `Creator for ${welcome.creator.price} a month`
      : `${welcome.price} a month`;
    const was = originalOf(k);
    if (welcome.why === "card")
      return `Your plan stopped because a payment didn't go through, not because you left. Pick up where you left off for ${offer}.`;
    if (welcome.why === "price" && was != null)
      return `You told us it cost too much, so here's less: ${offer} instead of ${fmtFor(k, was)}, for as long as you stay.`;
    if (welcome.why === "broken" && welcome.coversOk)
      return `You told us something wasn't working. In the last week ${welcome.coversOk} in 100 covers came out fine. Come back for ${offer}.`;
    return `${toCreator ? `Creator for ${welcome.creator.price}` : `Everything you had, for ${welcome.price}`} a month on a plan we don't advertise. It stays yours for as long as you're subscribed.`;
  };
  const voiceNames = shownVoices.map((v) => v.artistname).filter(Boolean);
  // Welcome back: their covers are the reason, unless a cover of theirs is
  // what's waiting (then that stays the headline).
  const welcomeCopy = welcome && !isCover;
  const headline =
    isCover && coverName
      ? songTitle(coverName)
      : welcomeCopy
        ? welcome.covers > 1
          ? `Your ${welcome.covers.toLocaleString()} covers are still here`
          : welcome.covers === 1
            ? "Your cover is still here"
            : "Make covers again"
        : reason.title;
  // Whose voices, when we know them; else the reason's line.
  const subline =
    isCover && voiceNames.length
      ? voiceNames.join(", ").replace(/, ([^,]*)$/, " & $1")
      : isCover
        ? "Your audio and voice are set up"
        : welcomeCopy
          ? welcomeLine()
          : reason.body;
  const plans = switchable.length ? switchable : planKey ? [planKey] : [];
  // The leave step's gift: Basic billed yearly, for people starting a plan
  // in a currency with annual prices. Per month against the standing
  // monthly price (not the first-month promo). Real annual price, no coupon.
  const GIFT_PLAN = "basic";
  const yearlyOffer = (() => {
    if (!startingNew || welcome || !isAnnualAvailable(currency)) return null;
    const price = getAnnualPrice(GIFT_PLAN, currency);
    const monthly = originalOf(PLANS[GIFT_PLAN]) ?? priceOf(PLANS[GIFT_PLAN]);
    if (!price || !monthly) return null;
    const save = Math.round((1 - price / (monthly * 12)) * 100);
    return save > 0 ? { price, perMonth: price / 12, monthly, save } : null;
  })();

  // A plan card: name, price today and after, one line. The recommended
  // plan is pre-selected and outlined in lime.
  const planCard = (k) => {
    const p = PLANS[k];
    const selected = planKey === k;
    const original = originalOf(p);
    return (
      <button
        key={k}
        type="button"
        aria-pressed={selected}
        onClick={() => setPlanKey(k)}
        className={`tw-relative tw-flex tw-w-full tw-cursor-pointer tw-flex-col tw-rounded-[22px] tw-border-2 tw-border-solid tw-p-3.5 tw-text-left tw-text-white tw-transition-colors md:tw-p-4 ${
          selected
            ? "tw-border-jam-lime tw-bg-[rgba(209,254,23,0.12)]"
            : "tw-border-white/20 tw-bg-transparent hover:tw-border-white/40"
        }`}
      >
        {k === defaultKey && (
          <span className="tw-absolute -tw-top-2.5 tw-right-3 tw-rounded-full tw-bg-jam-lime tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-extrabold tw-tracking-[0.04em] tw-text-jam-ink">
            RECOMMENDED
          </span>
        )}
        <span
          className={`tw-text-[15px] tw-font-extrabold ${selected ? "tw-text-jam-lime" : ""}`}
        >
          {p.label}
        </span>
        <span className="tw-mt-0.5 tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-1.5">
          <span className="jam-display tw-text-[24px] tw-font-extrabold tw-tracking-[-0.04em]">
            {fmtFor(p, priceOf(p))}
          </span>
          {/* First-month promo: the standing price struck through, and
              "first month" so the deal reads true; the renewal line under
              the button states the monthly price. */}
          {original != null ? (
            <>
              <span className="tw-text-[14px] tw-font-bold tw-text-jam-soft tw-line-through">
                {fmtFor(p, original)}
              </span>
              <span className="tw-basis-full tw-text-[11px] tw-font-bold tw-text-jam-soft">
                {welcomeOn(p)
                  ? "a month, for as long as you stay"
                  : "first month"}
              </span>
            </>
          ) : (
            <span className="tw-text-[11px] tw-font-bold tw-text-jam-soft">
              a month
            </span>
          )}
        </span>
      </button>
    );
  };

  // Welcome back: when their plan ended, and their latest covers as tiles
  // (the voice's picture and name), with how many more there are.
  const endedDays = welcome?.endedDays ?? null;
  const endedPill = welcomeCopy && endedDays != null && (
    <span className="tw-inline-flex tw-items-center tw-gap-2 tw-self-start tw-rounded-full tw-bg-white/[0.12] tw-px-3 tw-py-2 tw-text-xs tw-font-extrabold md:tw-px-4 md:tw-text-sm">
      <span className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-jam-lime" />
      Your plan ended{" "}
      {endedDays < 1
        ? "today"
        : endedDays === 1
          ? "yesterday"
          : endedDays < 60
            ? `${endedDays} days ago`
            : `${Math.round(endedDays / 30)} months ago`}
    </span>
  );
  const tiles = welcomeCopy ? (welcome.recent ?? []).slice(0, 3) : [];
  const moreCovers = (welcome?.covers ?? 0) - tiles.length;
  const coverTiles = tiles.length > 0 && (
    <div className="tw-flex tw-justify-center tw-gap-2 md:tw-justify-start md:tw-gap-3">
      {tiles.map((c, i) => (
        <span
          key={i}
          className="tw-relative tw-h-[92px] tw-w-[92px] tw-overflow-hidden tw-rounded-[22px] tw-bg-jam-deep md:tw-h-[140px] md:tw-w-[140px] md:tw-rounded-[30px]"
        >
          <VoiceImage
            model={c.voice}
            fill
            sizes="140px"
            className="tw-object-cover"
          />
          <span className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-bg-gradient-to-t tw-from-black/80 tw-to-transparent tw-px-2 tw-pb-1.5 tw-pt-6 tw-text-[11px] tw-font-extrabold tw-leading-tight md:tw-px-3 md:tw-pb-2.5 md:tw-text-[13px]">
            {/* The voice, not the song: cover names are often a file
                name or a prompt. */}
            <span className="tw-line-clamp-2">
              {voiceName(c.voice?.artistname)}
            </span>
          </span>
        </span>
      ))}
      {moreCovers > 0 && (
        <span className="tw-flex tw-h-[92px] tw-w-[92px] tw-flex-col tw-items-center tw-justify-center tw-rounded-[22px] tw-bg-jam-lime tw-text-jam-ink md:tw-h-[140px] md:tw-w-[140px] md:tw-rounded-[30px]">
          <span className="jam-display tw-text-[26px] tw-font-extrabold tw-leading-none md:tw-text-[40px]">
            +{moreCovers.toLocaleString()}
          </span>
          <span className="tw-mt-1 tw-text-[11px] tw-font-extrabold md:tw-text-[13px]">
            more
          </span>
        </span>
      )}
    </div>
  );

  const holdPill = holdEnds && (
    <span className="tw-inline-flex tw-items-center tw-gap-2 tw-self-start tw-rounded-full tw-bg-white/[0.12] tw-px-3 tw-py-2 tw-text-xs tw-font-extrabold md:tw-px-4 md:tw-text-sm">
      <Lock size={13} strokeWidth={3} className="tw-text-jam-lime" />
      Held for{" "}
      <span className="tw-tabular-nums tw-text-jam-lime">
        {formatHoldLeft(holdEnds)}
      </span>
    </span>
  );

  // Leaving, with a yearly offer to make: the whole screen becomes a gift.
  // A short wrapping animation, the box springs in, "Open my gift" shakes
  // and bursts it, then the offer. It is the real annual price (no coupon),
  // shown per month, with the yearly terms next to the button; no timer or
  // scarcity claims.
  const showGift = (leaving || giftClaiming) && !!yearlyOffer;
  const gift = PLANS[GIFT_PLAN];
  let giftScreen = null;
  if (showGift) {
    const CONFETTI = [
      [-120, -90],
      [110, -110],
      [-150, 10],
      [150, 20],
      [-90, 110],
      [100, 120],
      [-30, -150],
      [40, 150],
      [-170, -40],
      [170, -60],
      [0, -170],
      [-60, 160],
    ];
    giftScreen = (
      <div className="jg tw-relative tw-flex tw-min-h-[100dvh] tw-w-full tw-flex-col tw-overflow-clip tw-bg-[#120622] tw-text-white">
        <style>{`
          @keyframes jgDrop { 0% { transform: scale(.2) translateY(-60px) rotate(-14deg); opacity: 0 } 55% { transform: scale(1.12) rotate(4deg); opacity: 1 } 75% { transform: scale(.94) rotate(-2deg) } 100% { transform: scale(1) rotate(0) } }
          @keyframes jgFloat { 0%, 100% { transform: translateY(0) } 50% { transform: translateY(-12px) } }
          @keyframes jgShake { 0%, 100% { transform: rotate(0) scale(1) } 15% { transform: rotate(-14deg) scale(1.04) } 30% { transform: rotate(13deg) scale(1.08) } 45% { transform: rotate(-11deg) scale(1.12) } 60% { transform: rotate(9deg) scale(1.16) } 75% { transform: rotate(-5deg) scale(1.22) } 88% { transform: scale(1.32); opacity: 1 } 100% { transform: scale(.2); opacity: 0 } }
          @keyframes jgBurst { 0% { transform: scale(.3); opacity: .9 } 100% { transform: scale(3); opacity: 0 } }
          @keyframes jgSpin { to { transform: rotate(360deg) } }
          @keyframes jgPulse { 0%, 100% { opacity: .35; transform: scale(1) } 50% { opacity: .6; transform: scale(1.08) } }
          @keyframes jgPop { 0% { transform: translate(0, 0) scale(1); opacity: 0 } 15% { opacity: 1 } 100% { transform: translate(var(--dx), var(--dy)) scale(.3); opacity: 0 } }
          @keyframes jgRise { 0% { transform: translateY(18px) scale(.96); opacity: 0 } 100% { transform: none; opacity: 1 } }
          @media (prefers-reduced-motion: reduce) { .jg * { animation: none !important } }
        `}</style>
        <div
          className="tw-pointer-events-none tw-absolute tw-left-1/2 tw-top-[16%] tw-h-[520px] tw-w-[520px] -tw-ml-[260px] tw-rounded-full tw-bg-jam-purple tw-blur-[110px]"
          style={{ animation: "jgPulse 3.2s ease-in-out infinite" }}
        />
        <div className="tw-pointer-events-none tw-absolute tw-left-1/2 tw-top-[26%] tw-h-[260px] tw-w-[260px] -tw-translate-x-1/2 tw-rounded-full tw-bg-jam-lime tw-opacity-[0.18] tw-blur-[90px]" />
        {[
          [12, 14, 6],
          [82, 10, 4],
          [22, 38, 3],
          [88, 32, 5],
          [8, 62, 4],
          [74, 58, 3],
          [30, 80, 5],
          [90, 78, 4],
          [55, 8, 3],
          [48, 90, 4],
        ].map(([x, y, r], n) => (
          <span
            key={n}
            className={`tw-pointer-events-none tw-absolute tw-rounded-full ${n % 3 ? "tw-bg-jam-lime" : "tw-bg-white"} tw-opacity-60`}
            style={{ left: `${x}%`, top: `${y}%`, width: r * 2, height: r * 2 }}
          />
        ))}

        <div className="tw-relative tw-mx-auto tw-flex tw-w-full tw-max-w-[460px] tw-flex-1 tw-flex-col tw-px-[18px] tw-pt-16 md:tw-justify-center md:tw-pb-16">
          {giftPhase === "arriving" ? (
            <div className="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-gap-6 tw-pb-24 md:tw-flex-none md:tw-pb-0">
              <span className="tw-relative tw-flex tw-h-[120px] tw-w-[120px] tw-items-center tw-justify-center">
                <span
                  className="tw-absolute tw-inset-0"
                  style={{ animation: "jgSpin 1.1s linear infinite" }}
                >
                  {[0, 60, 120, 180, 240, 300].map((deg, n) => (
                    <span
                      key={deg}
                      className="tw-absolute tw-left-1/2 tw-top-0 tw-rounded-full tw-bg-jam-lime"
                      style={{
                        width: 10 - n,
                        height: 10 - n,
                        opacity: 1 - n * 0.13,
                        transformOrigin: "50% 60px",
                        transform: `translateX(-50%) rotate(${deg}deg)`,
                      }}
                    />
                  ))}
                </span>
                <span className="tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-[18px] tw-bg-white/10 tw-text-jam-lime">
                  <Gift size={28} strokeWidth={2.2} />
                </span>
              </span>
              <div className="tw-text-sm tw-font-extrabold tw-tracking-[0.04em] tw-text-jam-soft">
                Wrapping something for you…
              </div>
            </div>
          ) : giftPhase !== "open" ? (
            <div className="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-text-center md:tw-flex-none">
              <span className="tw-relative tw-mt-6 tw-flex tw-h-[148px] tw-w-[148px] tw-items-center tw-justify-center">
                {giftPhase === "opening" && (
                  <>
                    {[0, 0.18, 0.36].map((d) => (
                      <span
                        key={d}
                        className="tw-absolute tw-inset-0 tw-rounded-full tw-border-4 tw-border-solid tw-border-jam-lime"
                        style={{
                          animation: `jgBurst .9s ease-out ${0.55 + d}s both`,
                        }}
                      />
                    ))}
                    {CONFETTI.map(([dx, dy], n) => (
                      <span
                        key={n}
                        className={`tw-absolute tw-left-1/2 tw-top-1/2 tw-h-2.5 tw-w-2.5 ${n % 2 ? "tw-rounded-full tw-bg-jam-lime" : "tw-rounded-[2px] tw-bg-white"}`}
                        style={{
                          "--dx": `${dx}px`,
                          "--dy": `${dy}px`,
                          animation: `jgPop .8s cubic-bezier(.2,.7,.3,1) ${0.65 + (n % 4) * 0.04}s both`,
                        }}
                      />
                    ))}
                  </>
                )}
                <span
                  style={{
                    animation:
                      giftPhase === "opening"
                        ? "jgShake 1.05s ease-in both"
                        : "jgDrop .75s cubic-bezier(.2,.8,.3,1.2) both, jgFloat 2.6s ease-in-out .8s infinite",
                  }}
                  className="tw-flex tw-h-[148px] tw-w-[148px] tw-items-center tw-justify-center tw-rounded-[40px] tw-bg-jam-lime tw-text-jam-ink tw-shadow-[0_24px_80px_-10px_rgba(209,254,23,0.55)]"
                >
                  <Gift size={72} strokeWidth={2} />
                </span>
              </span>
              <div
                className="tw-mt-10 tw-text-xs tw-font-extrabold tw-tracking-[0.14em] tw-text-jam-lime"
                style={{ animation: "jgRise .5s ease-out .25s both" }}
              >
                BEFORE YOU GO
              </div>
              <div
                className="jam-display tw-mt-2 tw-text-[38px] tw-font-extrabold tw-leading-[1] tw-tracking-[-0.04em] md:tw-text-[46px]"
                style={{ animation: "jgRise .5s ease-out .35s both" }}
              >
                We&apos;ve saved you a gift
              </div>
              <p
                className="tw-m-0 tw-mt-3 tw-text-[15px] tw-font-semibold tw-text-jam-soft"
                style={{ animation: "jgRise .5s ease-out .45s both" }}
              >
                Open it to see what it is.
              </p>
              <div className={`${PINNED} tw-self-stretch md:tw-mt-8`}>
                <button
                  type="button"
                  disabled={giftPhase === "opening"}
                  onClick={() => setGiftPhase("opening")}
                  className="tw-h-[60px] tw-w-full tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-jam-lime tw-text-[18px] tw-font-extrabold tw-text-jam-ink hover:tw-bg-[#C4EE00] disabled:tw-cursor-default md:tw-h-16"
                >
                  {giftPhase === "opening" ? "Opening…" : "Open my gift"}
                </button>
                <button
                  type="button"
                  onClick={() => closeModal("upgrade-modal")}
                  className="tw-cursor-pointer tw-self-center tw-border-0 tw-bg-transparent tw-p-1 tw-text-xs tw-font-bold tw-text-jam-soft hover:tw-text-white"
                >
                  No thanks, leave
                </button>
              </div>
            </div>
          ) : (
            <div className="tw-flex tw-flex-1 tw-flex-col md:tw-flex-none">
              <div
                className="tw-flex tw-flex-col tw-items-center tw-text-center"
                style={{
                  animation: "jgRise .55s cubic-bezier(.2,.8,.3,1) both",
                }}
              >
                <span className="tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-[18px] tw-bg-jam-lime tw-text-jam-ink">
                  <Gift size={28} strokeWidth={2.2} />
                </span>
                <div className="tw-mt-5 tw-text-xs tw-font-extrabold tw-tracking-[0.14em] tw-text-jam-lime">
                  YOUR GIFT
                </div>
                <div className="jam-display tw-mt-2 tw-text-[36px] tw-font-extrabold tw-leading-[1] tw-tracking-[-0.04em] md:tw-text-[44px]">
                  {gift.label} for {fmt(yearlyOffer.perMonth)} a month
                </div>
                <p className="tw-m-0 tw-mt-3 tw-text-[15px] tw-font-semibold tw-text-jam-soft">
                  Unlimited covers with every voice, locked in for 12 months.
                </p>
              </div>

              <div
                className="tw-relative tw-mt-7 tw-rounded-[28px] tw-border-2 tw-border-solid tw-border-jam-lime tw-bg-[rgba(209,254,23,0.1)] tw-p-5 tw-shadow-[0_0_60px_-12px_rgba(209,254,23,0.45)]"
                style={{
                  animation: "jgRise .55s cubic-bezier(.2,.8,.3,1) .12s both",
                }}
              >
                <span className="tw-absolute -tw-top-3 tw-left-1/2 -tw-translate-x-1/2 tw-rounded-full tw-bg-jam-lime tw-px-3 tw-py-1 tw-text-[11px] tw-font-extrabold tw-tracking-[0.06em] tw-text-jam-ink">
                  SAVE {yearlyOffer.save}%
                </span>
                <div className="tw-flex tw-items-baseline tw-justify-center tw-gap-2">
                  <span className="jam-display tw-text-[56px] tw-font-extrabold tw-leading-none tw-tracking-[-0.05em]">
                    {fmt(yearlyOffer.perMonth)}
                  </span>
                  <span className="tw-text-lg tw-font-bold tw-text-jam-soft tw-line-through">
                    {fmt(yearlyOffer.monthly)}
                  </span>
                  <span className="tw-text-sm tw-font-bold tw-text-jam-soft">
                    /mo
                  </span>
                </div>
                <div className="tw-mt-1 tw-text-center tw-text-[13px] tw-font-bold tw-text-jam-soft">
                  {fmt(yearlyOffer.price)} billed yearly
                </div>
                <div className="tw-mt-4 tw-grid tw-grid-cols-2 tw-gap-x-2.5 tw-gap-y-2 tw-border-0 tw-border-t tw-border-solid tw-border-white/10 tw-pt-4">
                  {[
                    ...(reason === REASONS.unlock ? ["This cover, now"] : []),
                    ...HIGHLIGHTS[GIFT_PLAN],
                  ]
                    .slice(0, 4)
                    .map((h) => (
                      <span
                        key={h}
                        className="tw-flex tw-items-center tw-gap-2 tw-text-[13px] tw-font-bold"
                      >
                        <span className="tw-flex tw-h-[18px] tw-w-[18px] tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lime tw-text-jam-ink">
                          <Check size={11} strokeWidth={4} />
                        </span>
                        {h}
                      </span>
                    ))}
                </div>
              </div>

              <div className={`${PINNED} md:tw-mt-7`}>
                {clientSecret ? (
                  <button
                    type="button"
                    disabled
                    aria-label="Getting your checkout ready"
                    className="tw-flex tw-h-[60px] tw-w-full tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-jam-lime md:tw-h-16"
                  >
                    <Spinner
                      size={4}
                      className="tw-border-3 tw-border-jam-ink"
                    />
                  </button>
                ) : (
                  <PlanCTA
                    {...ctaProps(gift)}
                    billingInterval="annual"
                    onSelect={() => {
                      setSelectedPlan({ ...gift, annual: true });
                      setGiftClaiming(true);
                    }}
                    label={`Lock in ${fmt(yearlyOffer.perMonth)} a month`}
                    className="tw-h-[60px] !tw-py-0 tw-bg-jam-lime !tw-text-[18px] tw-text-jam-ink hover:tw-bg-[#C4EE00] md:tw-h-16"
                  />
                )}
                {/* Renewal terms next to the button, as on every step. */}
                <p className="tw-m-0 tw--mt-1 tw-text-center tw-text-xs tw-font-semibold tw-text-jam-soft">
                  {`${fmt(yearlyOffer.price)} billed today for 12 months. Renews yearly until you cancel.`}
                </p>
                <div className="tw-flex tw-items-center tw-justify-center tw-gap-4 tw-text-xs tw-font-bold">
                  <button
                    type="button"
                    onClick={() => setLeaving(false)}
                    className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-1 tw-font-extrabold tw-text-white"
                  >
                    See monthly plans
                  </button>
                  <button
                    type="button"
                    onClick={() => closeModal("upgrade-modal")}
                    className="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-1 tw-text-jam-soft hover:tw-text-white"
                  >
                    Leave anyway
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  if (giftScreen && !clientSecret) return giftScreen;
  const backdrop = shownVoices[1] || shownVoices[0] || tiles[0]?.voice;

  // The root clips (not hides) its overflow: hidden would make it a scroll
  // container and stop the phone CTA sticking to the dialog's bottom edge.
  return (
    <>
      {giftClaiming && giftScreen && (
        <div className="tw-fixed tw-inset-0 tw-z-30 tw-overflow-y-auto">
          {giftScreen}
        </div>
      )}
      <div className="tw-relative tw-min-h-[100dvh] tw-w-full tw-overflow-clip tw-bg-[#150828] tw-text-white">
        {/* The cover's voice, huge and blurred, as the backdrop. */}
        {backdrop ? (
          <div className="tw-pointer-events-none tw-absolute -tw-left-10 -tw-top-10 tw-h-[440px] tw-w-[calc(100%+80px)] tw-opacity-[0.35] tw-blur-[36px] md:tw-h-[110%] md:tw-w-[62%]">
            <VoiceImage
              model={backdrop}
              fill
              sizes="600px"
              className="tw-object-cover"
            />
          </div>
        ) : (
          <div className="tw-pointer-events-none tw-absolute -tw-left-24 -tw-top-24 tw-h-[420px] tw-w-[420px] tw-rounded-full tw-bg-jam-purple tw-opacity-40 tw-blur-[90px]" />
        )}
        <div className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-top-[200px] tw-h-[240px] tw-bg-gradient-to-b tw-from-transparent tw-to-[#150828] md:tw-hidden" />

        <div className="tw-relative tw-mx-auto tw-flex tw-min-h-[100dvh] tw-max-w-[1200px] tw-flex-col tw-gap-4 tw-px-[18px] tw-pt-4 md:tw-grid md:tw-grid-cols-[minmax(0,1fr)_540px] md:tw-content-center md:tw-items-center md:tw-gap-12 md:tw-px-12 lg:tw-grid-cols-[minmax(0,1fr)_600px] lg:tw-gap-16 md:tw-py-16 tw-pb-0">
          {/* Hero: the cover being unlocked, or why the paywall opened. On a
            phone it folds away at checkout so the form fits the screen. */}
          <div
            className={`tw-flex-col tw-gap-3.5 md:tw-flex md:tw-gap-6 ${clientSecret ? "tw-hidden" : "tw-flex"}`}
          >
            <div className="tw-flex tw-min-h-10 tw-items-center tw-pr-12 md:tw-pr-0">
              {holdPill || endedPill}
            </div>
            {coverTiles ? (
              coverTiles
            ) : shownVoices.length > 0 ? (
              <div className="tw-flex tw-items-end tw-justify-center md:tw-justify-start">
                {shownVoices.map((v, i) => {
                  const centre = shownVoices.length === 3 ? i === 1 : i === 0;
                  return (
                    <span
                      key={v._id || i}
                      style={{ zIndex: centre ? 5 : 4 - i }}
                      className={`tw-relative tw-overflow-hidden tw-rounded-full tw-border-4 tw-border-solid tw-bg-jam-deep md:tw-border-[6px] ${
                        centre
                          ? "tw-h-[94px] tw-w-[94px] tw-border-jam-lime md:tw-h-[156px] md:tw-w-[156px]"
                          : "tw-mb-0 tw-h-[70px] tw-w-[70px] tw-border-[#150828] md:tw-h-[116px] md:tw-w-[116px]"
                      } ${i > 0 ? "-tw-ml-4 md:-tw-ml-[26px]" : ""}`}
                    >
                      <VoiceImage
                        model={v}
                        fill
                        sizes="160px"
                        className="tw-object-cover"
                      />
                    </span>
                  );
                })}
              </div>
            ) : (
              <span className="tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-self-center tw-rounded-full tw-bg-jam-lime tw-text-jam-ink md:tw-h-20 md:tw-w-20 md:tw-self-start">
                <ReasonIcon size={30} strokeWidth={2.4} />
              </span>
            )}
            <div className="tw-text-center md:tw-text-left">
              {(isCover || welcome) && (
                <div className="tw-text-[11px] tw-font-extrabold tw-tracking-[0.12em] tw-text-jam-lime md:tw-text-sm">
                  {welcome ? "WELCOME BACK" : "YOUR COVER IS READY TO MAKE"}
                </div>
              )}
              <h2 className="jam-display tw-m-0 tw-mt-1.5 tw-text-[34px] tw-font-extrabold tw-leading-[0.98] tw-tracking-[-0.04em] md:tw-mt-3 md:tw-text-[72px]">
                {headline}
              </h2>
              <p className="tw-m-0 tw-mt-2 tw-text-[13px] tw-font-bold tw-text-jam-soft md:tw-mt-4 md:tw-text-lg">
                {subline}
              </p>
            </div>
            {coversToday ? (
              <div className="tw-hidden tw-items-center tw-gap-2 tw-text-sm tw-font-bold tw-text-jam-soft md:tw-flex">
                <span className="tw-relative tw-flex tw-h-2.5 tw-w-2.5">
                  <span className="tw-absolute tw-inline-flex tw-h-full tw-w-full tw-animate-ping tw-rounded-full tw-bg-[#22C55E] tw-opacity-60" />
                  <span className="tw-relative tw-inline-flex tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-[#22C55E]" />
                </span>
                <span>
                  <span className="tw-font-extrabold tw-text-white">
                    {coversToday.toLocaleString()}
                  </span>{" "}
                  covers made on Jammable today
                </span>
              </div>
            ) : null}
          </div>

          {/* The offer. A glass card on desktop, the lower half on phones. */}
          <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-3.5 md:tw-flex-none md:tw-gap-4 md:tw-rounded-[40px] md:tw-border md:tw-border-solid md:tw-border-white/10 md:tw-bg-white/[0.07] md:tw-p-8 lg:tw-p-10">
            {clientSecret && !leaving ? (
              <>
                {/* Phone: what's being bought, compact, above the form. */}
                <div className="tw-flex tw-items-center tw-gap-3 tw-pr-12 md:tw-hidden">
                  {shownVoices.length > 0 && (
                    <span className="tw-flex tw-flex-shrink-0">
                      {shownVoices.map((v, i) => (
                        <span
                          key={v._id || i}
                          className={`tw-relative tw-h-10 tw-w-10 tw-overflow-hidden tw-rounded-full tw-border-[3px] tw-border-solid tw-border-[#150828] tw-bg-jam-deep ${i ? "-tw-ml-3" : ""}`}
                        >
                          <VoiceImage
                            model={v}
                            fill
                            sizes="40px"
                            className="tw-object-cover"
                          />
                        </span>
                      ))}
                    </span>
                  )}
                  <span className="tw-min-w-0">
                    <span className="jam-display tw-block tw-truncate tw-text-[19px] tw-font-extrabold tw-leading-tight">
                      {headline}
                    </span>
                    {holdEnds && (
                      <span className="tw-text-xs tw-font-bold tw-text-jam-soft">
                        Held for{" "}
                        <span className="tw-tabular-nums tw-text-jam-lime">
                          {formatHoldLeft(holdEnds)}
                        </span>
                      </span>
                    )}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setClientSecret(null)}
                  className="tw-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-self-start tw-border-0 tw-bg-transparent tw-p-0 tw-text-[13px] tw-font-extrabold tw-text-jam-soft hover:tw-text-white"
                >
                  <ArrowLeft size={15} strokeWidth={2.75} />
                  Change plan
                </button>
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-[22px] tw-bg-white/[0.07] tw-px-4 tw-py-3.5">
                  <span className="tw-min-w-0">
                    <span className="tw-block tw-text-[15px] tw-font-extrabold">
                      {selectedPlan?.label}
                      {selectedPlan?.annual ? ", yearly" : " plan"}
                    </span>
                    {/* Prices after today (the monthly price after the first
                      month, the yearly total) are stated once, under the pay
                      button with the renewal terms. */}
                    {startingNew && selectedPlan?.annual && (
                      <span className="tw-block tw-text-xs tw-font-bold tw-text-jam-soft">
                        Billed yearly
                      </span>
                    )}
                  </span>
                  <span className="tw-flex-shrink-0 tw-text-right">
                    <span className="jam-display tw-block tw-text-[26px] tw-font-extrabold tw-leading-none tw-tracking-[-0.04em]">
                      {fmtFor(
                        selectedPlan,
                        selectedPlan?.annual
                          ? checkoutPrice / 12
                          : checkoutPrice,
                      )}
                    </span>
                    <span className="tw-text-[11px] tw-font-bold tw-text-jam-soft">
                      {selectedPlan?.annual ? "a month" : "today"}
                    </span>
                  </span>
                </div>
                {/* The form fills the rest of the screen so its pay button
                  can pin to the bottom on phones, exactly like the plan
                  step's button, with the renewal terms under it. */}
                <div className="tw-flex tw-flex-1 tw-flex-col md:tw-flex-none">
                  <CreditCheckout
                    dark
                    clientSecret={clientSecret}
                    symbol={getCurrencySymbol(curOf(selectedPlan))}
                    currency={curOf(selectedPlan)}
                    price={checkoutPrice}
                    submitLabel={isCover ? "Pay and unlock" : "Pay"}
                    formClassName="tw-flex tw-flex-1 tw-flex-col"
                    submitWrapClassName={PINNED}
                    onReady={() => setPaymentReady(true)}
                    // Wallets first for people a card already let down, or
                    // who left this checkout once.
                    express={
                      via === "checkout-return" || welcome?.why === "card"
                    }
                    footer={
                      <>
                        {startingNew && selectedPlan && (
                          <p className="tw-m-0 tw--mt-1 tw-text-center tw-text-xs tw-font-semibold tw-text-jam-soft">
                            {selectedPlan.annual
                              ? `${fmtFor(selectedPlan, checkoutPrice)} billed today for 12 months. Renews yearly until you cancel.`
                              : renewalOf(selectedPlan)}
                          </p>
                        )}
                        <div className="tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-text-[11px] tw-font-bold tw-text-jam-soft md:tw-text-xs">
                          <Lock size={12} strokeWidth={2.6} />
                          Secure checkout with Stripe · cancel any time
                        </div>
                      </>
                    }
                  />
                </div>
              </>
            ) : leaving ? (
              // Leaving: how far along the cover is, what unlocking gets them,
              // and the real hold time if there is one; no invented urgency.
              // (The cover itself is already on the left / above.)
              <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-4 tw-pt-1 md:tw-flex-none md:tw-pb-1">
                <div className="tw-flex tw-flex-col tw-gap-1 tw-text-center md:tw-text-left">
                  <div className="jam-display tw-text-[28px] tw-font-extrabold tw-leading-tight">
                    Leave without your cover?
                  </div>
                  <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-jam-soft">
                    You&apos;re one step away.
                  </p>
                </div>

                {/* How far along it is. */}
                <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
                  {[
                    { done: true, text: "Your audio is in" },
                    {
                      done: true,
                      text: voiceNames.length
                        ? `${subline} ${voiceNames.length > 1 ? "are" : "is"} ready to sing it`
                        : "Your voice is picked",
                    },
                    { done: false, text: "Make the cover: about a minute" },
                  ].map((step) => (
                    <li
                      key={step.text}
                      className="tw-flex tw-items-center tw-gap-3 tw-text-sm tw-font-bold"
                    >
                      <span
                        className={`tw-flex tw-h-6 tw-w-6 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full ${step.done ? "tw-bg-jam-lime tw-text-jam-ink" : "tw-border-2 tw-border-dashed tw-border-white/40"}`}
                      >
                        {step.done && <Check size={13} strokeWidth={4} />}
                      </span>
                      <span className={step.done ? "" : "tw-text-jam-soft"}>
                        {step.text}
                      </span>
                    </li>
                  ))}
                </ol>

                {/* What unlocking gets them, beyond this cover. */}
                {rec && (
                  <div className="tw-flex tw-flex-col tw-gap-2.5 tw-rounded-[22px] tw-bg-white/[0.07] tw-p-4">
                    <div className="tw-text-xs tw-font-extrabold tw-tracking-[0.06em] tw-text-jam-soft">
                      UNLOCKING GETS YOU
                    </div>
                    <div className="tw-grid tw-grid-cols-2 tw-gap-x-2.5 tw-gap-y-2">
                      {["This cover, now", ...HIGHLIGHTS[rec.plan]]
                        .slice(0, 4)
                        .map((h) => (
                          <span
                            key={h}
                            className="tw-flex tw-items-center tw-gap-2 tw-text-[13px] tw-font-bold"
                          >
                            <Sparkles
                              size={13}
                              strokeWidth={2.75}
                              className="tw-flex-shrink-0 tw-text-jam-lime"
                            />
                            {h}
                          </span>
                        ))}
                    </div>
                  </div>
                )}

                {holdEnds && (
                  <p className="tw-m-0 tw-text-center tw-text-[13px] tw-font-semibold tw-text-jam-soft md:tw-text-left">
                    It&apos;s held for{" "}
                    <span className="tw-tabular-nums tw-text-jam-lime">
                      {formatHoldLeft(holdEnds)}
                    </span>
                    . After that it&apos;s gone and you&apos;d have to make it
                    again.
                  </p>
                )}

                {/* Pinned to the bottom on phones, like the other steps. */}
                <div className={PINNED}>
                  <button
                    type="button"
                    onClick={() => setLeaving(false)}
                    className="tw-h-[60px] tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-jam-lime tw-text-[17px] tw-font-extrabold tw-text-jam-ink hover:tw-bg-[#C4EE00]"
                  >
                    Unlock my cover
                  </button>
                  <button
                    type="button"
                    onClick={() => closeModal("upgrade-modal")}
                    className="tw-cursor-pointer tw-self-center tw-border-0 tw-bg-transparent tw-p-1 tw-text-xs tw-font-bold tw-text-jam-soft hover:tw-text-white"
                  >
                    Leave anyway
                  </button>
                </div>
              </div>
            ) : rec ? (
              <>
                <div className="jam-display tw-hidden tw-text-[32px] tw-font-extrabold tw-leading-none tw-tracking-[-0.03em] md:tw-block lg:tw-text-[36px]">
                  {isCover ? "Pick a plan to unlock it" : "Pick a plan"}
                </div>
                <div className="tw-flex tw-flex-col tw-gap-2.5 tw-rounded-[22px] tw-bg-white/[0.07] tw-p-4">
                  <div className="tw-text-xs tw-font-extrabold tw-tracking-[0.06em] tw-text-jam-soft">
                    WHAT YOU GET WITH {rec.label.toUpperCase()}
                  </div>
                  <div className="tw-grid tw-grid-cols-2 tw-gap-x-2.5 tw-gap-y-2">
                    {[
                      ...(reason === REASONS.unlock ? ["This cover, now"] : []),
                      ...HIGHLIGHTS[rec.plan],
                    ]
                      .slice(0, 6)
                      .map((h) => (
                        <span
                          key={h}
                          className="tw-flex tw-items-center tw-gap-2 tw-text-[13px] tw-font-bold md:tw-text-sm"
                        >
                          <span className="tw-flex tw-h-[18px] tw-w-[18px] tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lime tw-text-jam-ink">
                            <Check size={11} strokeWidth={4} />
                          </span>
                          {h}
                        </span>
                      ))}
                  </div>
                </div>
                {/* Phone: the plan cards, the button and its terms stay pinned
                  to the bottom of the screen over a fade while the rest of
                  the offer scrolls. */}
                <div className={PINNED}>
                  <div
                    className={`tw-grid tw-gap-2 ${plans.length > 1 ? "tw-grid-cols-2" : "tw-grid-cols-1"}`}
                  >
                    {plans.map(planCard)}
                  </div>
                  <PlanCTA
                    {...ctaProps(rec)}
                    label={recLabel}
                    className="tw-h-[60px] !tw-py-0 tw-bg-jam-lime !tw-text-[18px] tw-text-jam-ink hover:tw-bg-[#C4EE00] md:tw-h-16"
                  />
                  {/* The renewal terms must be shown before payment (ROSCA and
                  state auto-renewal laws): one line, next to the button. */}
                  {startingNew && (
                    <p className="tw-m-0 tw--mt-1.5 tw-text-center tw-text-xs tw-font-semibold tw-text-jam-soft">
                      {renewalOf(rec)}
                    </p>
                  )}
                  <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-x-4 tw-gap-y-1 tw-text-[11px] tw-font-bold tw-text-jam-soft md:tw-text-xs">
                    {coversToday ? (
                      <span className="tw-flex tw-items-center tw-gap-1.5 md:tw-hidden">
                        <span className="tw-h-[7px] tw-w-[7px] tw-rounded-full tw-bg-[#22C55E]" />
                        {coversToday.toLocaleString()} covers made today
                      </span>
                    ) : null}
                    <span>Secure checkout with Stripe · cancel any time</span>
                  </div>
                </div>
              </>
            ) : (
              <div className="tw-flex tw-flex-col tw-gap-3 tw-text-center">
                <div className="jam-display tw-text-[26px] tw-font-extrabold">
                  You&apos;re on our top plan
                </div>
                <Link
                  href="/settings"
                  onClick={() => closeModal("upgrade-modal")}
                  className="tw-text-sm tw-font-extrabold tw-text-jam-lime tw-no-underline"
                >
                  Manage your plan
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default UpgradeModal;
